/**
 * Contacts-local helper for deriving saved-view column sets from
 * per-field `savedViewMembership` declarations.
 *
 * Each contact field declares which saved views include it via
 * `savedViewMembership`. The saved-view config then derives its
 * `visibleColumns` by calling this helper. Editing the field declaration is
 * the single source of truth for inclusion; the saved view is derived.
 *
 * Result order: by default the helper returns fields in `Object.keys(fields)`
 * order (declaration order across the merged `contact-fields-*.ts` maps). For
 * views whose historical column order diverges from declaration order, pass
 * an explicit `order` array — the helper still validates that `order` and
 * `savedViewMembership` agree, so the per-field declarations remain the
 * source of truth for column inclusion.
 */
import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";

/**
 * Returns the ordered list of field ids whose `savedViewMembership`
 * includes `viewId`. `TId` is a literal field-id union (e.g.
 * `ContactFieldId`) so the returned array is compile-time compatible with
 * `SavedViewConfig<TId>["visibleColumns"]`.
 *
 * @param viewId  Saved view id, e.g. `"all-contacts"`.
 * @param fields  Map of contact field id → field config.
 * @param order   Optional explicit ordering. When provided the result is
 *                returned in this exact order. The helper throws if `order`
 *                references an id whose field config does not list `viewId`
 *                in its `savedViewMembership`, or if a member field is
 *                missing from `order` — surfacing drift between per-field
 *                declarations and per-view ordering at module load time.
 */
export function collectFieldsForSavedView<TId extends string>(
  viewId: string,
  fields: Readonly<Record<TId, SimpleFieldConfig>>,
  order?: readonly TId[],
): TId[] {
  const members: TId[] = [];
  for (const key of Object.keys(fields) as TId[]) {
    const membership = fields[key].savedViewMembership;
    if (membership && membership.includes(viewId)) {
      members.push(key);
    }
  }

  if (!order) return members;

  const memberSet = new Set<string>(members);
  for (const id of order) {
    if (!memberSet.has(id)) {
      throw new Error(
        `[contact-saved-view-helpers] View "${viewId}" lists "${id}" in its order, ` +
          `but the field's savedViewMembership does not include "${viewId}".`,
      );
    }
  }
  const orderSet = new Set<string>(order);
  for (const id of members) {
    if (!orderSet.has(id)) {
      throw new Error(
        `[contact-saved-view-helpers] Field "${id}" declares savedViewMembership ` +
          `for "${viewId}" but is missing from the view's order array.`,
      );
    }
  }
  return [...order];
}
